/* eslint-disable unicorn/numeric-separators-style */
/* eslint-disable no-prototype-builtins */
import { useEffect, useState } from 'react';
import { ICategory, IProductList } from '../models';
import Filters from './Products/Filters';
import MobileFilters from './Products/Filters/MobileFilters';
import ProductTile from './ProductTile';
import useQueryParams from '../hooks/useQueryParams';
import { fetchProducts } from '../fetchers/products';
import { getLangCountry } from '../helpers/languages';
import Spinner from './Spinner';
import { useTranslation } from 'react-i18next';
import { getToken } from '../helpers/auth';
import ProductsSort from './Products/Filters/Sort';
import Breadcrumbs from './Breadcrumbs';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import dynamic from 'next/dynamic';
import CategoryTile from './CategoryTile';
import { groupProductsByCategory } from '../helpers/products';
// import ModalNewsLetter from './ModalNewsletter';
import CategoryImage from './CategoryImage';
import { classNames } from '../helpers/classes';
import { fetchShippingCountriesList } from '../fetchers/shipping';
import { getCurrencyName } from '../helpers/prices';
import { useRouter } from 'next/router';
import { AdjustmentsIcon } from '@heroicons/react/solid';
import parse from 'html-react-parser';

const ImageWithFallback = dynamic(
  () => import('../components/ImageWithFallback'),
  { ssr: false }
);

const LIMIT_DEFAULT = 1000000;
const SLICED_DEFAULT = 15;

export default function CategoryPage({ category }: { category: ICategory }) {
  const { t } = useTranslation();
  const router = useRouter();
  const [query] = useQueryParams();
  const [products, setProducts] = useState<(IProductList | ICategory)[]>([]);
  const [count, setCount] = useState({ productCount: 0, categoryCount: 0 });
  const [loading, setLoading] = useState(false);
  const token = getToken();
  const { slug = [] } = query;
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showDesktopFilters, setShowDesktopFilters] = useState(true);
  const [sliced, setSliced] = useState(SLICED_DEFAULT);
  const { lang, country } = getLangCountry(query);
  const countries = fetchShippingCountriesList({ lang });
  const currencyCode = getCurrencyName(countries, country);

  const hasNext =
    sliced + SLICED_DEFAULT <=
    count.productCount + count.categoryCount + SLICED_DEFAULT;

  const loadMore = () => {
    setSliced(sliced + SLICED_DEFAULT);
  };

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasNext,
    onLoadMore: loadMore
  });

  useEffect(() => {
    if (router.isReady) {
      setSliced(SLICED_DEFAULT);
      setProducts([]);
      getProducts();
    }
  }, [query]);

  const { filters, breadcrumbs, extreme = false } = category || {};
  const { categories } = filters || {};
  const getApiParams = () => {
    const {
      color,
      size,
      sort,
      fit,
      temperature,
      insulationlevel,
      activityintensity,
      sockscushion,
      socksthickness,
      socksheight,
      sockscompression
    } = query;
    return {
      ...getLangCountry(query),
      token,
      offset: 0,
      limit: LIMIT_DEFAULT,
      category: Array.isArray(slug) ? slug.join('/') : slug,
      color,
      size,
      sort,
      fit,
      temperature,
      insulationlevel,
      activityintensity,
      sockscushion,
      socksthickness,
      socksheight,
      sockscompression
    };
  };

  const getProducts = async () => {
    try {
      const params = getApiParams();
      setLoading(true);
      window.scrollTo(0, 0);
      const { products: resProducts = [], count } = await fetchProducts(params);
      const groupped = groupProductsByCategory(resProducts, category);
      setProducts(groupped);
      setCount({
        productCount: count,
        categoryCount: groupped.filter(p => p.hasOwnProperty('category_name'))
          .length
      });
      trackPageViewEvent(groupped);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const trackPageViewEvent = (groupped: (IProductList | ICategory)[]) => {
    const impressions = groupped
      .filter(element => !element.hasOwnProperty('category_name'))
      .slice(0, SLICED_DEFAULT)
      .map((product: any, position) => ({
        item_id: product.product_type + '-' + product.selling_code,
        item_name: product.name,
        discount:
          product.price?.sale_price && product.price?.price
            ? product.price?.price - product.price?.sale_price
            : 0,
        index: SLICED_DEFAULT * position + 1,
        item_brand: 'Jeep',
        item_category: Array.isArray(categories)
          ? categories.join('/')
          : categories,
        item_list_id: 'category_page',
        item_list_name: 'Category page',
        price: product.price?.sale_price || product.price?.price || 0,
        quantity: 1
      }));
    window.dataLayer?.push({ ecommerce: null });
    window.dataLayer?.push({
      event: 'view_item_list',
      ecommerce: {
        item_list_id: 'category_page',
        item_list_name: 'Category page',
        currencyCode,
        items: impressions
      }
    });
  };

  const trackClickEvent = (p: IProductList, position: number) => {
    window.dataLayer?.push({ ecommerce: null });
    window.dataLayer?.push({
      event: 'select_item',
      ecommerce: {
        currencyCode,
        item_list_id: 'category_page',
        item_list_name: 'Category page',
        items: [
          {
            item_id: p.product_type + '-' + p.selling_code,
            item_name: p.name,
            discount:
              p.price?.sale_price && p.price?.price
                ? p.price?.price - p.price?.sale_price
                : 0,
            index: position + 1,
            item_brand: 'Jeep',
            item_category: Array.isArray(categories)
              ? categories.join('/')
              : categories,
            item_list_id: 'category_page',
            item_list_name: 'Category page',
            price: p.price?.sale_price || p.price?.price || 0,
            quantity: 1
          }
        ]
      }
    });
  };

  const {
    banner = false,
    image = '',
    name = false,
    category_name = ''
  } = category;
  return (
    <>
      {/* <ModalNewsLetter /> */}
      <div className="w-full overflow-hidden bg-cover bg-no-repeat">
        <ImageWithFallback
          src={image}
          fallback="/img/category-fallback.jpeg"
          alt={name || category_name}
        />
      </div>

      <div className={extreme ? 'cat-dark' : 'bg-grey-custom'}>
        <div>
          <MobileFilters
            filters={filters}
            show={mobileFiltersOpen}
            close={() => setMobileFiltersOpen(false)}
            disabled={loading}
          />

          <main className="mx-auto px-4 sm:px-6 lg:px-12">
            <div className="relative flex items-baseline flex-col sm:flex-row justify-between pt-12 pb-6">
              <div>
                <h1
                  className={classNames(
                    extreme ? 'text-orange' : 'text-primary',
                    'text-4xl font-bold sm:text-8xl font-sans flex items-center'
                  )}
                >
                  {parse(category.name)}
                  {/* <div className="text-lg lg:text-xl lg:relative lg:top-3 tracking-normal bg-black text-primary px-2 py-1 lg:px-3 lg:py-2 ml-4 rounded-sm">
                    <span className="animate-pulse font-bold">-40%</span>
                  </div> */}
                  {/* <span className="text-4xl align-top">
                    ( {count.productCount} )
                  </span> */}
                </h1>

                <div className="breadcrumb flex items-center mt-5">
                  <Breadcrumbs
                    breadcrumbs={breadcrumbs}
                    ui={extreme ? 'xtreme' : ''}
                  />
                </div>
              </div>

              <div className="flex items-center mt-6 sm:mt-0">
                <ProductsSort ui={extreme ? 'xtreme' : ''} />

                <button
                  type="button"
                  className={`font-bold text-sm p-2 -m-2 ml-4 sm:ml-6 opacity-60 lg:hidden ${
                    extreme ? 'text-white' : 'text-black'
                  }`}
                  onClick={() => setMobileFiltersOpen(true)}
                >
                  <span>{t('FILTER')}</span>
                  <AdjustmentsIcon className="w-4 h-4 inline-block ml-2" />
                </button>
                {/* <button
                  type="button"
                  className={`font-bold text-sm hidden p-2 -m-2 ml-4 sm:ml-6 text-black opacity-60 lg:block ${
                    extreme ? 'text-white' : 'text-black'
                  }`}
                  onClick={() => setShowDesktopFilters(!showDesktopFilters)}
                >
                  <span>{t('FILTER')}</span>
                  <AdjustmentsIcon className="w-4 h-4 inline-block ml-2" />
                </button> */}
              </div>
            </div>

            <section aria-labelledby="products-heading" className="pt-6 pb-24">
              <h2 id="products-heading" className="sr-only">
                {t('PRODUCTS')}
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-x-8 gap-y-10">
                <form
                  className={`hidden ${showDesktopFilters ? 'lg:block' : ''}`}
                >
                  <Filters
                    filters={filters}
                    disabled={loading}
                    ui={extreme ? 'xtreme' : ''}
                  />
                </form>

                <div
                  className={`lg:col-span-${showDesktopFilters ? '4' : '5'}`}
                >
                  {!loading && products.length === 0 && (
                    <p className="text-center opacity-60">{t('NO_PRODUCTS')}</p>
                  )}
                  <div className="grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-3 lg:grid-cols-3 xl:gap-x-4">
                    {products.slice(0, sliced).map((p, i) => {
                      if (p.hasOwnProperty('category_name')) {
                        return (
                          <CategoryTile
                            category={p as ICategory}
                            siblings={categories?.filter(
                              ({ id_category }) =>
                                id_category !== (p as ICategory).id_category
                            )}
                            key={i}
                          />
                        );
                      }
                      return (
                        <ProductTile
                          product={p as IProductList}
                          key={i}
                          onClick={() => trackClickEvent(p as IProductList, i)}
                          ui={extreme ? 'xtreme' : ''}
                        />
                      );
                    })}
                  </div>

                  {hasNext && <div ref={sentryRef} />}

                  <div className="w-full text-center py-8">
                    {loading && <Spinner />}
                  </div>
                </div>
              </div>
            </section>
          </main>

          <CategoryImage
            title={banner ? banner.title : ''}
            description={banner ? banner.text : ''}
            image={banner ? banner.image : ''}
            fullHeight
            extreme={extreme}
          />
        </div>
      </div>
    </>
  );
}
